import axios from 'axios';

const adminUrl = process.env.VUE_APP_ADMIN_URL;

/**
 * Turns an axios error into the service's error shape, keeping the backend's
 * error code so the caller can react to it (e.g. a duplicate person).
 */
function toError(err) {
  const error = err.response?.data?.error || {};
  return {
    status: 'error',
    code: error.code,
    message: error.message || err.message || 'Error desconocido',
    fields: error.fields,
    existingRatingId: error.existingRatingId
  };
}

/**
 * NPS ratings of a contract. Every method returns the contract's refreshed
 * NPS detail on success, so the modal and the table row can update from a
 * single response.
 */
export default class ContratoNpsService {
  /**
   * Current NPS, company NPS and history by measurement period.
   * @param {string} contratoId
   * @returns {Promise<{status: 'ok', data: object}|{status: 'error', code?: string, message: string}>}
   */
  static async getDetail(contratoId) {
    try {
      const accessToken = JSON.parse(localStorage.getItem('accessToken'));
      const response = await axios.get(`${adminUrl}/contratos/${contratoId}/nps`, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      return { status: 'ok', data: response.data.data };
    } catch (err) {
      return toError(err);
    }
  }

  /**
   * Records one counterpart's answer.
   * @param {string} contratoId
   * @param {{personName: string, personRole: string, score: number, period: string}} rating
   */
  static async createRating(contratoId, rating) {
    try {
      const accessToken = JSON.parse(localStorage.getItem('accessToken'));
      const response = await axios.post(`${adminUrl}/contratos/${contratoId}/nps-ratings`, rating, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      return { status: 'ok', data: response.data.data };
    } catch (err) {
      return toError(err);
    }
  }

  /**
   * Corrects an answer (full replacement).
   * @param {string} contratoId
   * @param {string} ratingId
   * @param {{personName: string, personRole: string, score: number, period: string}} rating
   */
  static async updateRating(contratoId, ratingId, rating) {
    try {
      const accessToken = JSON.parse(localStorage.getItem('accessToken'));
      const response = await axios.put(
        `${adminUrl}/contratos/${contratoId}/nps-ratings/${ratingId}`,
        rating,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      return { status: 'ok', data: response.data.data };
    } catch (err) {
      return toError(err);
    }
  }

  /**
   * Removes an answer entered by mistake.
   * @param {string} contratoId
   * @param {string} ratingId
   */
  static async deleteRating(contratoId, ratingId) {
    try {
      const accessToken = JSON.parse(localStorage.getItem('accessToken'));
      const response = await axios.delete(`${adminUrl}/contratos/${contratoId}/nps-ratings/${ratingId}`, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      return { status: 'ok', data: response.data.data };
    } catch (err) {
      return toError(err);
    }
  }
}
